import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Calendar as CalIcon,
  Clock,
  Video,
  MapPin,
} from 'lucide-react';

import * as cal from '../../api/calendar';
import EventModal from '../../components/calendar/EventModal';

type ViewMode = 'month' | 'week' | 'day';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function startOfMonthGrid(date: Date): Date {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return startOfDay(start);
}

function buildDays(start: Date, count: number): Date[] {
  const out: Date[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push(d);
  }
  return out;
}

function fmtTime(d: Date): string {
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

interface EventAccent {
  bar: string;          // background of the colour bar / dot
  chipBg: string;       // chip background
  chipBorder: string;   // chip hairline
  chipText: string;     // chip text
  label: string;        // human-readable type label
}

function eventAccent(ev: cal.CalendarEvent): EventAccent {
  if (ev.source_type === 'chat_meeting' || !!ev.meet_url) {
    return {
      bar: 'var(--gc-accent)',
      chipBg: 'var(--gc-accent-soft)',
      chipBorder: 'rgba(16, 185, 129, 0.22)',
      chipText: 'var(--gc-accent-strong)',
      label: 'Meeting',
    };
  }
  if (ev.source_type === 'task') {
    return {
      bar: '#c47a3a',
      chipBg: 'rgba(196, 122, 58, 0.12)',
      chipBorder: 'rgba(196, 122, 58, 0.28)',
      chipText: '#7a4517',
      label: 'Task',
    };
  }
  return {
    bar: 'var(--gc-primary)',
    chipBg: 'var(--gc-primary-container)',
    chipBorder: 'var(--gc-divider)',
    chipText: 'var(--gc-primary)',
    label: 'Event',
  };
}

export default function CalendarPage() {
  const [events, setEvents] = useState<cal.CalendarEvent[]>([]);
  const [view, setView] = useState<ViewMode>('month');
  const [date, setDate] = useState<Date>(new Date());
  const [selected, setSelected] = useState<cal.CalendarEvent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchEvents = useCallback(async () => {
    const from = new Date(date.getFullYear(), date.getMonth() - 1, 1);
    const to = new Date(date.getFullYear(), date.getMonth() + 2, 0, 23, 59, 59);
    setLoading(true);
    try {
      const items = await cal.listEvents(from, to);
      setEvents(items);
      setError(null);
    } catch (e: any) {
      console.error('[Calendar] fetch failed:', e);
      setError(e?.response?.data?.detail || e?.message || 'Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const eventsByDay = useMemo(() => {
    const map = new Map<string, cal.CalendarEvent[]>();
    for (const ev of events) {
      const start = new Date(ev.start_at);
      const key = `${start.getFullYear()}-${start.getMonth()}-${start.getDate()}`;
      const arr = map.get(key) || [];
      arr.push(ev);
      map.set(key, arr);
    }
    return map;
  }, [events]);

  const navigatePrev = () => {
    const d = new Date(date);
    if (view === 'month') d.setMonth(d.getMonth() - 1);
    else if (view === 'week') d.setDate(d.getDate() - 7);
    else d.setDate(d.getDate() - 1);
    setDate(d);
  };

  const navigateNext = () => {
    const d = new Date(date);
    if (view === 'month') d.setMonth(d.getMonth() + 1);
    else if (view === 'week') d.setDate(d.getDate() + 7);
    else d.setDate(d.getDate() + 1);
    setDate(d);
  };

  const goToday = () => setDate(new Date());

  const headerLabel =
    view === 'month'
      ? `${MONTHS[date.getMonth()]} ${date.getFullYear()}`
      : view === 'week'
      ? (() => {
          const start = new Date(date);
          start.setDate(date.getDate() - date.getDay());
          const end = new Date(start);
          end.setDate(start.getDate() + 6);
          return `${start.toLocaleDateString([], { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}`;
        })()
      : date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  const today = new Date();

  const upcoming = useMemo(() => {
    const now = new Date();
    return events
      .filter(ev => new Date(ev.end_at) >= now)
      .sort((a, b) => a.start_at.localeCompare(b.start_at))
      .slice(0, 5);
  }, [events]);

  return (
    <div
      className="py-6 gc-fade-in"
      style={{
        fontFamily: 'var(--gc-font)',
        color: 'var(--gc-text-primary)',
      }}
    >
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 13,
              background: 'var(--gc-primary-container)',
              color: 'var(--gc-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'var(--gc-elev-1)',
              flexShrink: 0,
            }}
          >
            <CalIcon size={20} strokeWidth={2.2} />
          </div>
          <div>
            <span
              className="gc-eyebrow flex items-center gap-1.5 mb-1"
              style={{ color: 'var(--gc-text-tertiary)' }}
            >
              <span className="gc-leaf" aria-hidden />
              <span>Volume II · Calendar</span>
            </span>
            <h1
              style={{
                fontFamily: 'var(--gc-font-display)',
                fontWeight: 700,
                fontSize: 24,
                color: 'var(--gc-text-primary)',
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
              }}
            >
              Calendar
            </h1>
            <p
              className="text-xs mt-1"
              style={{ color: 'var(--gc-text-tertiary)' }}
            >
              {events.length} event{events.length === 1 ? '' : 's'} in view
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={goToday}
            style={{
              padding: '7px 14px',
              fontSize: 12.5,
              fontFamily: 'var(--gc-font-display)',
              fontWeight: 600,
              borderRadius: 11,
              border: '1px solid var(--gc-border)',
              background: 'var(--gc-surface-0)',
              color: 'var(--gc-text-secondary)',
              cursor: 'pointer',
              transition: 'all 180ms ease',
            }}
            onMouseOver={e => {
              e.currentTarget.style.background = 'var(--gc-surface-2)';
              e.currentTarget.style.color = 'var(--gc-text-primary)';
            }}
            onMouseOut={e => {
              e.currentTarget.style.background = 'var(--gc-surface-0)';
              e.currentTarget.style.color = 'var(--gc-text-secondary)';
            }}
          >
            Today
          </button>

          <div
            className="flex items-center overflow-hidden"
            style={{
              border: '1px solid var(--gc-border)',
              background: 'var(--gc-surface-0)',
              borderRadius: 11,
            }}
          >
            <button
              onClick={navigatePrev}
              className="p-2 transition-colors"
              style={{ color: 'var(--gc-text-secondary)', background: 'transparent' }}
              onMouseOver={e => { e.currentTarget.style.background = 'var(--gc-surface-2)'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
              aria-label="Previous"
            >
              <ChevronLeft size={16} />
            </button>
            <div
              style={{
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 700,
                fontFamily: 'var(--gc-font-display)',
                letterSpacing: '-0.005em',
                color: 'var(--gc-text-primary)',
                minWidth: 200,
                textAlign: 'center',
                borderLeft: '1px solid var(--gc-divider)',
                borderRight: '1px solid var(--gc-divider)',
              }}
            >
              {headerLabel}
            </div>
            <button
              onClick={navigateNext}
              className="p-2 transition-colors"
              style={{ color: 'var(--gc-text-secondary)', background: 'transparent' }}
              onMouseOver={e => { e.currentTarget.style.background = 'var(--gc-surface-2)'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
              aria-label="Next"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          <div className="gc-segmented" role="tablist" aria-label="Calendar view">
            {(['month', 'week', 'day'] as const).map(v => (
              <button
                key={v}
                role="tab"
                aria-selected={view === v}
                onClick={() => setView(v)}
                className={`gc-segmented-pill ${view === v ? 'is-active' : ''}`}
              >
                {v[0].toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>

          <button
            onClick={fetchEvents}
            className="p-2 transition-colors"
            style={{
              borderRadius: 11,
              border: '1px solid var(--gc-border)',
              background: 'var(--gc-surface-0)',
              color: 'var(--gc-text-secondary)',
              cursor: 'pointer',
            }}
            onMouseOver={e => { e.currentTarget.style.background = 'var(--gc-surface-2)'; }}
            onMouseOut={e => { e.currentTarget.style.background = 'var(--gc-surface-0)'; }}
            title="Refresh"
            aria-label="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {error && (
        <div
          className="mb-4 p-3"
          style={{
            borderRadius: 12,
            background: 'rgba(220, 38, 38, 0.08)',
            border: '1px solid rgba(220, 38, 38, 0.18)',
            color: 'var(--gc-dnd)',
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-6">
        {/* Calendar surface */}
        <div
          className="overflow-hidden"
          style={{
            background: 'var(--gc-surface-0)',
            border: '1px solid var(--gc-border)',
            borderRadius: 20,
            boxShadow: '0 1px 2px rgba(15, 29, 20, 0.04)',
          }}
        >
          {view === 'month' && (
            <MonthView
              date={date}
              today={today}
              eventsByDay={eventsByDay}
              onSelectEvent={setSelected}
            />
          )}
          {view === 'week' && (
            <WeekView
              date={date}
              today={today}
              events={events}
              onSelectEvent={setSelected}
            />
          )}
          {view === 'day' && (
            <DayView
              date={date}
              events={events}
              onSelectEvent={setSelected}
            />
          )}
        </div>

        {/* Side panel */}
        <aside className="space-y-4">
          <div
            style={{
              background: 'var(--gc-surface-0)',
              border: '1px solid var(--gc-border)',
              borderRadius: 18,
              padding: 16,
              boxShadow: '0 1px 2px rgba(15, 29, 20, 0.04)',
            }}
          >
            <div className="flex items-center gap-1.5 mb-3" style={{ color: 'var(--gc-text-tertiary)' }}>
              <span className="gc-leaf" aria-hidden style={{ width: 5, height: 5 }} />
              <span className="gc-eyebrow">Upcoming</span>
            </div>
            {upcoming.length === 0 ? (
              <div className="text-center py-8" style={{ color: 'var(--gc-text-tertiary)' }}>
                <Clock size={26} strokeWidth={1.5} className="mx-auto mb-2 opacity-50" />
                <p className="text-xs">No upcoming events</p>
              </div>
            ) : (
              <ul className="space-y-1">
                {upcoming.map(ev => {
                  const accent = eventAccent(ev);
                  const start = new Date(ev.start_at);
                  return (
                    <li key={ev.id}>
                      <button
                        onClick={() => setSelected(ev)}
                        className="w-full text-left flex gap-3 p-2.5 transition-colors"
                        style={{ borderRadius: 12, background: 'transparent' }}
                        onMouseOver={e => { e.currentTarget.style.background = 'var(--gc-surface-2)'; }}
                        onMouseOut={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <span
                          className="rounded-full flex-shrink-0"
                          style={{ width: 3, background: accent.bar }}
                        />
                        <div className="flex-1 min-w-0">
                          <p
                            className="truncate"
                            style={{
                              fontSize: 13,
                              fontWeight: 600,
                              fontFamily: 'var(--gc-font-display)',
                              letterSpacing: '-0.005em',
                              color: 'var(--gc-text-primary)',
                            }}
                          >
                            {ev.title}
                          </p>
                          <p
                            className="mt-0.5"
                            style={{ fontSize: 11.5, color: 'var(--gc-text-tertiary)' }}
                          >
                            {start.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} · {fmtTime(start)}
                          </p>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div
            style={{
              background: 'var(--gc-surface-0)',
              border: '1px solid var(--gc-border)',
              borderRadius: 18,
              padding: 16,
              boxShadow: '0 1px 2px rgba(15, 29, 20, 0.04)',
            }}
          >
            <div className="flex items-center gap-1.5 mb-3" style={{ color: 'var(--gc-text-tertiary)' }}>
              <span className="gc-leaf" aria-hidden style={{ width: 5, height: 5 }} />
              <span className="gc-eyebrow">Legend</span>
            </div>
            <ul className="space-y-2" style={{ fontSize: 13, color: 'var(--gc-text-secondary)' }}>
              <li className="flex items-center gap-2.5">
                <span className="rounded-full" style={{ width: 10, height: 10, background: 'var(--gc-accent)' }} />
                <span>Meeting (from chat)</span>
              </li>
              <li className="flex items-center gap-2.5">
                <span className="rounded-full" style={{ width: 10, height: 10, background: 'var(--gc-primary)' }} />
                <span>Manual event</span>
              </li>
              <li className="flex items-center gap-2.5">
                <span className="rounded-full" style={{ width: 10, height: 10, background: '#c47a3a' }} />
                <span>Task (due)</span>
              </li>
            </ul>
          </div>
        </aside>
      </div>

      {selected && (
        <EventModal
          event={selected}
          onClose={() => setSelected(null)}
          onChanged={() => { setSelected(null); fetchEvents(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────── Month View ───────────────────────────────────────────
function MonthView({
  date, today, eventsByDay, onSelectEvent,
}: {
  date: Date; today: Date; eventsByDay: Map<string, cal.CalendarEvent[]>;
  onSelectEvent: (ev: cal.CalendarEvent) => void;
}) {
  const start = startOfMonthGrid(date);
  const days = buildDays(start, 42);
  const currentMonth = date.getMonth();

  return (
    <div>
      <div
        className="grid grid-cols-7"
        style={{
          background: 'var(--gc-surface-1)',
          borderBottom: '1px solid var(--gc-divider)',
        }}
      >
        {DAY_LABELS.map(l => (
          <div
            key={l}
            className="text-center"
            style={{
              padding: '10px 12px',
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--gc-text-tertiary)',
              fontFamily: 'var(--gc-font-display)',
            }}
          >
            {l}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 auto-rows-[minmax(120px,1fr)]">
        {days.map((d, idx) => {
          const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
          const dayEvents = eventsByDay.get(key) || [];
          const inMonth = d.getMonth() === currentMonth;
          const isToday = isSameDay(d, today);
          return (
            <div
              key={idx}
              className="p-1.5 transition-colors"
              style={{
                borderRight: '1px solid var(--gc-divider)',
                borderBottom: '1px solid var(--gc-divider)',
                background: inMonth ? 'var(--gc-surface-0)' : 'var(--gc-surface-1)',
                opacity: inMonth ? 1 : 0.6,
              }}
              onMouseOver={e => {
                if (inMonth) e.currentTarget.style.background = 'var(--gc-surface-2)';
              }}
              onMouseOut={e => {
                e.currentTarget.style.background = inMonth ? 'var(--gc-surface-0)' : 'var(--gc-surface-1)';
              }}
            >
              <div className="flex justify-end mb-1">
                <span
                  className="inline-flex items-center justify-center"
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 700,
                    fontFamily: 'var(--gc-font-display)',
                    background: isToday ? 'var(--gc-primary)' : 'transparent',
                    color: isToday
                      ? 'var(--gc-on-primary)'
                      : inMonth
                        ? 'var(--gc-text-primary)'
                        : 'var(--gc-text-disabled)',
                    boxShadow: isToday ? '0 2px 6px rgba(15, 29, 20, 0.15)' : 'none',
                  }}
                >
                  {d.getDate()}
                </span>
              </div>
              <div className="flex flex-col gap-1 max-h-[78px] overflow-hidden">
                {dayEvents.slice(0, 3).map(ev => {
                  const accent = eventAccent(ev);
                  const dim = ev.response === 'DECLINED' ? 0.5 : ev.response === 'TENTATIVE' ? 0.75 : 1;
                  return (
                    <button
                      key={ev.id}
                      onClick={() => onSelectEvent(ev)}
                      className="flex items-center gap-1.5 truncate text-left transition-all"
                      style={{
                        padding: '3px 8px',
                        borderRadius: 7,
                        fontSize: 10.5,
                        background: accent.chipBg,
                        color: accent.chipText,
                        border: '1px solid ' + accent.chipBorder,
                        opacity: dim,
                        textDecoration: ev.response === 'DECLINED' ? 'line-through' : 'none',
                        fontFamily: 'var(--gc-font)',
                        cursor: 'pointer',
                      }}
                      onMouseOver={e => { e.currentTarget.style.transform = 'translateX(2px)'; }}
                      onMouseOut={e => { e.currentTarget.style.transform = 'translateX(0)'; }}
                    >
                      <span
                        className="rounded-full flex-shrink-0"
                        style={{ width: 5, height: 5, background: accent.bar }}
                      />
                      <span style={{ fontWeight: 700 }}>{fmtTime(new Date(ev.start_at))}</span>
                      <span className="truncate">{ev.title}</span>
                    </button>
                  );
                })}
                {dayEvents.length > 3 && (
                  <span
                    className="px-2"
                    style={{ fontSize: 10.5, color: 'var(--gc-text-tertiary)' }}
                  >
                    +{dayEvents.length - 3} more
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────── Week View ───────────────────────────────────────────
function WeekView({
  date, today, events, onSelectEvent,
}: {
  date: Date; today: Date; events: cal.CalendarEvent[];
  onSelectEvent: (ev: cal.CalendarEvent) => void;
}) {
  const start = new Date(date);
  start.setDate(date.getDate() - date.getDay());
  start.setHours(0, 0, 0, 0);
  const days = buildDays(start, 7);

  const weekEvents = events.filter(ev => {
    const d = new Date(ev.start_at);
    return d >= start && d < new Date(start.getTime() + 7 * 86400_000);
  });

  return (
    <div className="grid grid-cols-7">
      {days.map((d, idx) => {
        const isToday = isSameDay(d, today);
        const dayEvents = weekEvents
          .filter(ev => isSameDay(new Date(ev.start_at), d))
          .sort((a, b) => a.start_at.localeCompare(b.start_at));
        return (
          <div
            key={idx}
            className="last:border-r-0 min-h-[480px] flex flex-col"
            style={{ borderRight: '1px solid var(--gc-divider)' }}
          >
            <div
              className="text-center"
              style={{
                padding: '12px',
                borderBottom: '1px solid var(--gc-divider)',
                background: 'var(--gc-surface-1)',
              }}
            >
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--gc-text-tertiary)',
                  fontFamily: 'var(--gc-font-display)',
                }}
              >
                {DAY_LABELS[idx]}
              </div>
              <div
                className="inline-flex items-center justify-center mt-1.5"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  fontSize: 16,
                  fontWeight: 700,
                  fontFamily: 'var(--gc-font-display)',
                  background: isToday ? 'var(--gc-primary)' : 'transparent',
                  color: isToday ? 'var(--gc-on-primary)' : 'var(--gc-text-primary)',
                  boxShadow: isToday ? '0 2px 6px rgba(15, 29, 20, 0.15)' : 'none',
                }}
              >
                {d.getDate()}
              </div>
            </div>
            <div className="p-2 flex flex-col gap-1.5 flex-1">
              {dayEvents.length === 0 ? (
                <div
                  className="flex-1 flex items-center justify-center"
                  style={{ fontSize: 12, color: 'var(--gc-text-disabled)' }}
                >
                  —
                </div>
              ) : (
                dayEvents.map(ev => {
                  const accent = eventAccent(ev);
                  return (
                    <button
                      key={ev.id}
                      onClick={() => onSelectEvent(ev)}
                      className="text-left transition-all"
                      style={{
                        padding: '8px 10px',
                        borderRadius: 11,
                        background: accent.chipBg,
                        color: accent.chipText,
                        border: '1px solid ' + accent.chipBorder,
                        opacity: ev.response === 'DECLINED' ? 0.5 : 1,
                        textDecoration: ev.response === 'DECLINED' ? 'line-through' : 'none',
                        cursor: 'pointer',
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(15, 29, 20, 0.06)';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <div style={{ fontSize: 10.5, fontWeight: 700, fontFamily: 'var(--gc-font-display)' }}>
                        {fmtTime(new Date(ev.start_at))}
                      </div>
                      <div
                        className="truncate"
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: 'var(--gc-font-display)',
                          letterSpacing: '-0.005em',
                          marginTop: 2,
                        }}
                      >
                        {ev.title}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────── Day View ───────────────────────────────────────────
function DayView({
  date, events, onSelectEvent,
}: {
  date: Date; events: cal.CalendarEvent[];
  onSelectEvent: (ev: cal.CalendarEvent) => void;
}) {
  const dayEvents = events
    .filter(ev => isSameDay(new Date(ev.start_at), date))
    .sort((a, b) => a.start_at.localeCompare(b.start_at));

  return (
    <div className="p-6 min-h-[480px]">
      {dayEvents.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-20">
          <div
            className="flex items-center justify-center mb-3"
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: 'var(--gc-primary-container)',
              color: 'var(--gc-primary)',
            }}
          >
            <CalIcon size={24} strokeWidth={1.6} />
          </div>
          <p
            style={{
              fontSize: 14,
              fontWeight: 700,
              fontFamily: 'var(--gc-font-display)',
              color: 'var(--gc-text-primary)',
              letterSpacing: '-0.005em',
            }}
          >
            Nothing scheduled
          </p>
          <p
            className="mt-1"
            style={{ fontSize: 12, color: 'var(--gc-text-tertiary)' }}
          >
            {date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {dayEvents.map(ev => {
            const accent = eventAccent(ev);
            const start = new Date(ev.start_at);
            const end = new Date(ev.end_at);
            return (
              <button
                key={ev.id}
                onClick={() => onSelectEvent(ev)}
                className="flex items-stretch gap-3 transition-all text-left"
                style={{
                  padding: 16,
                  borderRadius: 14,
                  border: '1px solid var(--gc-border)',
                  background: 'var(--gc-surface-0)',
                  opacity: ev.response === 'DECLINED' ? 0.6 : 1,
                  cursor: 'pointer',
                }}
                onMouseOver={e => {
                  e.currentTarget.style.boxShadow = '0 6px 16px rgba(15, 29, 20, 0.08), 0 1px 2px rgba(15, 29, 20, 0.04)';
                  e.currentTarget.style.borderColor = 'var(--gc-primary-container)';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = 'var(--gc-border)';
                }}
              >
                <div
                  className="rounded-full flex-shrink-0"
                  style={{ width: 4, background: accent.bar }}
                />
                <div className="flex flex-col justify-center" style={{ minWidth: 110 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      fontFamily: 'var(--gc-font-display)',
                      color: 'var(--gc-text-primary)',
                    }}
                  >
                    {fmtTime(start)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--gc-text-tertiary)' }}>
                    to {fmtTime(end)}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      fontFamily: 'var(--gc-font-display)',
                      color: 'var(--gc-text-primary)',
                      letterSpacing: '-0.005em',
                      textDecoration: ev.response === 'DECLINED' ? 'line-through' : 'none',
                    }}
                  >
                    {ev.title}
                  </div>
                  {ev.description && (
                    <div
                      className="line-clamp-2 mt-1"
                      style={{ fontSize: 12, color: 'var(--gc-text-tertiary)' }}
                    >
                      {ev.description}
                    </div>
                  )}
                  <div
                    className="flex flex-wrap items-center gap-3 mt-2"
                    style={{ fontSize: 11, color: 'var(--gc-text-tertiary)' }}
                  >
                    {ev.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={11} /> {ev.location}
                      </span>
                    )}
                    {ev.meet_url && (
                      <span
                        className="inline-flex items-center gap-1"
                        style={{ color: 'var(--gc-accent-strong)', fontWeight: 600 }}
                      >
                        <Video size={11} /> Meet link
                      </span>
                    )}
                  </div>
                </div>
                <span
                  className="self-start"
                  style={{
                    fontSize: 9.5,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontFamily: 'var(--gc-font-display)',
                    padding: '4px 9px',
                    borderRadius: 999,
                    background: accent.chipBg,
                    color: accent.chipText,
                    border: '1px solid ' + accent.chipBorder,
                  }}
                >
                  {accent.label}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
